import type { ReactNode } from 'react';
import { createContext, use, useEffect } from 'react';
import { getStorageValue, useLocalStorage } from '@/hooks/browser/use-local-storage';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';
import { authStore } from '@/services/auth/store';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useStore } from '@nanostores/react';
import type { ColorSchemePreference } from '@customerio/pluma-components/react';

export const GlobalCSSVariable = {
  MainNavWidth: '--hydra-main-nav-width',
  MainNavTransitionTime: '--hydra-main-nav-transition-time', // set in index.scss, not usually set dynamically
  MainNavTransitionCurve: '--hydra-main-nav-transition-curve', // set in index.scss, not usually set dynamically
  TopNavHeight: '--hydra-top-nav-height',
  MasqBannerHeight: '--hydra-masq-banner-height',
  AssistantFooterHeight: '--hydra-assistant-footer-height',
  WorkspaceBorderHeight: '--hydra-workspace-border-height',
  WorkspaceBorderColor: '--hydra-workspace-border-color',
  WorkspaceDeliveryModeBannerHeight: '--hydra-workspace-delivery-mode-banner-height',
} as const;

type GlobalCSSVariable = (typeof GlobalCSSVariable)[keyof typeof GlobalCSSVariable];

interface IThemeContext {
  colorScheme: ColorSchemePreference;
  setColorScheme: (colorScheme: ColorSchemePreference) => void;
  canToggleTheme: boolean;
  setVariable: (variable: GlobalCSSVariable, value: string) => void;
  setVariables: (variables: Partial<Record<GlobalCSSVariable, string>>) => void;
  getVariable: (variable: GlobalCSSVariable) => string;
  removeVariable: (variable: GlobalCSSVariable) => void;
}

const ThemeContext = createContext<IThemeContext | null>(null);

export function useThemeContext() {
  const context = use(ThemeContext);
  if (context == null) {
    throw new Error('useThemeContext must be used within a ThemeContext');
  }
  return context;
}

const COLOR_SCHEME_KEY = 'color-scheme';
const COLOR_SCHEME_EXPLICIT_KEY = 'color-scheme-explicit';
const DEFAULT_COLOR_SCHEME: ColorSchemePreference = 'auto';

// One-time migration from the previous boolean-based keys
// (`dark-mode-enabled` / `dark-mode-explicit`) to the new
// `color-scheme` / `color-scheme-explicit` keys. Runs at module load
// before `useLocalStorage` reads, so existing user preferences carry
// across the rename without treating the previous implicit default as
// an explicit light preference.
(function migrateLegacyDarkModeKeys() {
  try {
    if (localStorage.getItem(COLOR_SCHEME_KEY) != null) return;

    const legacyValue = localStorage.getItem('dark-mode-enabled');
    if (legacyValue == null) return;

    const wasDark = JSON.parse(legacyValue) === true;
    const hadExplicitPreference = localStorage.getItem('dark-mode-explicit') != null;

    if (wasDark || hadExplicitPreference) {
      localStorage.setItem(COLOR_SCHEME_KEY, JSON.stringify(wasDark ? 'dark' : 'light'));
    }

    if (hadExplicitPreference) {
      localStorage.setItem(COLOR_SCHEME_EXPLICIT_KEY, 'true');
    }

    localStorage.removeItem('dark-mode-enabled');
    localStorage.removeItem('dark-mode-explicit');
  } catch {
    // localStorage may be unavailable (private mode, blocked) — skip migration
  }
})();

type ThemeContextProviderProps = { children: ReactNode };

export function getHasExplicitColorSchemePreference() {
  return getStorageValue<boolean>(COLOR_SCHEME_EXPLICIT_KEY, false);
}

export function ThemeContextProvider({ children }: ThemeContextProviderProps) {
  const isDarkModeToggleFlagEnabled = useFeatureFlag(FeatureFlag.DarkModeToggle);
  const auth = useStore(authStore);
  const isDarkModeToggleEnabled = isDarkModeToggleFlagEnabled || isBuilderPlan(auth.account);
  const hasExplicitColorSchemePreference = getHasExplicitColorSchemePreference();
  const canUseSelectedColorScheme = isDarkModeToggleEnabled === true || hasExplicitColorSchemePreference === true;
  const [colorScheme, setColorScheme] = useLocalStorage<ColorSchemePreference>(COLOR_SCHEME_KEY, DEFAULT_COLOR_SCHEME);

  // Apply product defaults only until the user chooses a color scheme.
  useEffect(() => {
    if (auth.account == null) return;

    if (hasExplicitColorSchemePreference === true) return;

    if (isBuilderPlan(auth.account)) {
      if (colorScheme !== 'dark') {
        setColorScheme('dark');
      }
      return;
    }

    if (isDarkModeToggleFlagEnabled && colorScheme === 'light') {
      setColorScheme(DEFAULT_COLOR_SCHEME);
    }
  }, [auth.account, colorScheme, hasExplicitColorSchemePreference, isDarkModeToggleFlagEnabled, setColorScheme]);

  const setColorSchemeExplicitly = (value: ColorSchemePreference) => {
    localStorage.setItem(COLOR_SCHEME_EXPLICIT_KEY, 'true');
    setColorScheme(value);
  };

  const setVariable = (variable: GlobalCSSVariable, value: string) => {
    document.documentElement.style.setProperty(variable, value);
  };

  const setVariables = (variables: Partial<Record<GlobalCSSVariable, string>>) => {
    for (const [variable, value] of Object.entries(variables)) {
      setVariable(variable as GlobalCSSVariable, value);
    }
  };

  const getVariable = (variable: GlobalCSSVariable) => {
    return document.documentElement.style.getPropertyValue(variable);
  };

  const removeVariable = (variable: GlobalCSSVariable) => {
    document.documentElement.style.removeProperty(variable);
  };

  return (
    <ThemeContext.Provider
      value={{
        colorScheme: canUseSelectedColorScheme ? colorScheme : 'auto',
        setColorScheme: setColorSchemeExplicitly,
        canToggleTheme: canUseSelectedColorScheme,
        setVariable,
        setVariables,
        getVariable,
        removeVariable,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
